import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default function OriginSection() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      gsap.from(".origin-text", {
        y: 60,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 70%" },
      });
      gsap.from(".origin-img", {
        clipPath: "inset(100% 0 0 0)",
        duration: 1.4,
        ease: "power4.out",
        scrollTrigger: { trigger: ".origin-img-wrap", start: "top 75%" },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="bg-bone py-20 md:py-28 px-6 md:px-10">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5 origin-img-wrap order-2 lg:order-1">
          <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden bg-sand">
            <div className="origin-img absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=portrait%20of%20woman%20textile%20designer%20examining%20natural%20fabric%20swatches%20in%20warm%20natural%20light%20studio%2C%20earth%20tone%20clothing%2C%20sustainable%20fashion%20atelier%20documentary%20photography%2C%20cream%20linen%20backdrop%2C%20intimate%20warm%20mood%2C%20artisanal%20craft%20detail%2C%20soft%20golden%20light&width=900&height=1100&seq=verdant-origin&orientation=portrait"
                alt="The founders"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-ink/70 via-ink/20 to-transparent">
              <p className="text-bone text-sm font-light">Margarida & Tomás</p>
              <p className="text-bone/70 text-xs mt-1">Co-founders, 2017</p>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 origin-text order-1 lg:order-2">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-ink/50 mb-6">
            ✦ How it began
          </p>
          <h2 className="font-display text-4xl md:text-6xl text-ink tracking-tightest font-light leading-[0.95]">
            A barn, a question,<br />
            <span className="font-display text-rust">and six garments.</span>
          </h2>
          <p className="mt-8 text-ink/70 text-base md:text-lg leading-relaxed max-w-lg font-light">
            In 2017, Margarida Ferreira and Tomás Oliveira met at a regenerative agriculture conference in Lisbon.
            Margarida had spent a decade in luxury fashion supply chains. Tomás was a textile scientist studying
            soil microbiology. They shared one conviction: the clothes we wear should leave the land better than they found it.
          </p>
          <p className="mt-6 text-ink/70 text-base md:text-lg leading-relaxed max-w-lg font-light">
            They rented a stone barn outside Braga, bought three secondhand sewing machines, and made six linen dresses.
            Each one took seventeen hours. They sold them at a farmers market. Every buyer asked the same question:
            "When will you make more?"
          </p>
          <div className="mt-10 flex items-center gap-8">
            <div>
              <p className="font-display text-4xl md:text-5xl text-ink tracking-tightest font-light">8</p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ink/50 font-mono">Years</p>
            </div>
            <div>
              <p className="font-display text-4xl md:text-5xl text-ink tracking-tightest font-light">47</p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ink/50 font-mono">Hours per piece</p>
            </div>
            <div>
              <p className="font-display text-4xl md:text-5xl text-ink tracking-tightest font-light">3</p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ink/50 font-mono">Continents</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}